import { cn } from '@/lib/utils';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { THEMES, LAYOUTS, DENSITIES, PAPERS } from '../rateChartConfig';
import { LayoutThumb, ThemeSwatch } from '../RateChartThumbs';
import { GroupTitle, OptionButton, tileCls } from '@/components/common/EditorFields';

/** Colour, layout, text size, columns and paper. */
export default function DesignPanel({ theme, layout, density, cols, paper, onChange }) {
    const { t } = useTranslation();

    const pickCard = (active) => cn('rounded-xl border-2 p-1.5 transition-colors', tileCls(active));
    const tileLabel = (active) => cn('text-[11px] font-medium text-center mt-1.5 leading-tight', active ? 'text-primary' : 'text-zinc-500 dark:text-zinc-400');

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2.5">
                <GroupTitle>{t('rateChart.design.colour')}</GroupTitle>
                <div className="grid grid-cols-3 gap-2.5">
                    {THEMES.map((th) => (
                        <button key={th.id} type="button" onClick={() => onChange({ theme: th.id })} aria-pressed={theme === th.id} className={pickCard(theme === th.id)}>
                            <ThemeSwatch themeId={th.id} />
                            <div className={tileLabel(theme === th.id)}>{t(`rateChart.themes.${th.id}`)}</div>
                        </button>
                    ))}
                </div>
            </section>

            <section className="flex flex-col gap-2.5">
                <GroupTitle>{t('rateChart.design.layout')}</GroupTitle>
                <div className="grid grid-cols-3 gap-2.5">
                    {LAYOUTS.map((ly) => (
                        <button key={ly.id} type="button" onClick={() => onChange({ layout: ly.id })} aria-pressed={layout === ly.id} className={pickCard(layout === ly.id)}>
                            <LayoutThumb layoutId={ly.id} themeId={theme} />
                            <div className={tileLabel(layout === ly.id)}>{t(`rateChart.layouts.${ly.id}`)}</div>
                        </button>
                    ))}
                </div>
            </section>

            <section className="flex flex-col gap-2.5">
                <GroupTitle>{t('rateChart.design.textSize')}</GroupTitle>
                <div className="flex gap-2">
                    {DENSITIES.map((d) => (
                        <OptionButton key={d.id} active={density === d.id} onClick={() => onChange({ density: d.id })} className="flex-1 min-h-[60px] flex flex-col items-center justify-center gap-1">
                            <span className="font-bold leading-none [font-family:'Anek_Bangla','Hind_Siliguri',sans-serif]" style={{ fontSize: d.sample }}>অ</span>
                            <span className="text-xs font-medium">{t(`rateChart.sizes.${d.id}`)}</span>
                        </OptionButton>
                    ))}
                </div>
            </section>

            <section className="flex flex-col gap-2.5">
                <GroupTitle>{t('rateChart.design.columns')}</GroupTitle>
                <div className="flex gap-2">
                    {[1, 2].map((c) => (
                        <OptionButton key={c} active={cols === c} onClick={() => onChange({ cols: c })} className="flex-1 py-2">{t(`rateChart.cols.${c}`)}</OptionButton>
                    ))}
                </div>
            </section>

            <section className="flex flex-col gap-2.5">
                <GroupTitle>{t('rateChart.design.paper')}</GroupTitle>
                <div className="flex gap-2">
                    {Object.keys(PAPERS).map((id) => (
                        <OptionButton key={id} active={paper === id} onClick={() => onChange({ paper: id })} className="flex-1 py-2">{id}</OptionButton>
                    ))}
                </div>
            </section>
        </div>
    );
}
